import { FunctionComponent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { useCheckoutSelector } from '../reducers/root';
import { PreQuoteTitle } from '../shared/delivery/PreQuoteTitle';

import { CheckoutDeliveryReview_item$key } from './__generated__/CheckoutDeliveryReview_item.graphql';
import { CheckoutDeliveryReview_shipmentQuote$key } from './__generated__/CheckoutDeliveryReview_shipmentQuote.graphql';
import { CheckoutDeliveryReview_transaction$key } from './__generated__/CheckoutDeliveryReview_transaction.graphql';

type CheckoutDeliveryReviewProps = {
    item: CheckoutDeliveryReview_item$key;
    transaction: CheckoutDeliveryReview_transaction$key | null;
    shipmentQuote: CheckoutDeliveryReview_shipmentQuote$key | null;
};

export const CheckoutDeliveryReview: FunctionComponent<CheckoutDeliveryReviewProps> = props => {
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);
    const offerCurrency = useCheckoutSelector(state => state.transaction.offerCurrency);

    const item = useFragment(
        graphql`
            fragment CheckoutDeliveryReview_item on Item {
                vertical
            }
        `,
        props.item
    );
    const vertical = item?.vertical;

    const shipmentQuote = useFragment(
        graphql`
            fragment CheckoutDeliveryReview_shipmentQuote on ShipmentQuote {
                ...PreQuoteTitle_shipmentQuote
            }
        `,
        props.shipmentQuote
    );

    const transaction = useFragment(
        graphql`
            fragment CheckoutDeliveryReview_transaction on Transaction {
                ...PreQuoteTitle_transaction
                sku {
                    ...PreQuoteTitle_sku
                }
            }
        `,
        props.transaction
    );

    const isQuoteNeeded = shipmentType === 'FIRSTDIBS_FACILITATED' && !shipmentQuoteId;
    const isLocalPickup = shipmentType === 'LOCAL_PICKUP';
    const isExternallyArranged = shipmentType === 'EXTERNALLY_ARRANGED';

    if (isQuoteNeeded) {
        return (
            <FormattedMessage
                id="checkout.CheckoutDeliveryReview.quote_needed"
                defaultMessage="{vertical, select, art {Gallery} other {Seller}} will send shipping quote within 48 hours"
                values={{ vertical }}
            />
        );
    } else if (isLocalPickup) {
        return (
            <FormattedMessage
                id="checkout.CheckoutDeliveryReview.local_pickup"
                defaultMessage="I'll pick up myself"
            />
        );
    } else if (isExternallyArranged) {
        return (
            <FormattedMessage
                id="checkout.CheckoutDeliveryReview.externally_arranged"
                defaultMessage="Arrange shipping yourself"
            />
        );
    } else if (shipmentQuote) {
        return (
            <PreQuoteTitle
                shipmentQuote={shipmentQuote}
                transaction={transaction}
                sku={transaction?.sku || null}
                offerCurrency={offerCurrency}
            />
        );
    } else {
        return null;
    }
};
